import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';

import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { DossierSignatureApiObject, TiersAppDispatch } from '@europrocurement/l2d-domain';
import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    Legend,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { FaOptionIcon, newSignIcon, signNamedIconsListe } from '@europrocurement/l2d-icons';

import { useClickActions, useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useDispatch } from 'react-redux';
import { PossibleTypes, useSearchBar } from '@europrocurement/l2d-modules';
import { getDossierSignature, MainDossierSignatureSelector } from '../../../../redux/subReducers';
import { useAppSelector } from '../../../../redux/types';
import { signatureListeFullPath, signatureNewFullPath } from '../../constants/paths';
import SignatureStatus from '../../components/SignatureStatus';
import DossierSignatureListeActions from './DossierSignatureListeActions';

/**
 * Convertit une date ISO en date locale formatée.
 */
const isoToLocalDate = (dateIso: string): string =>
    new Date(Date.parse(dateIso)).toLocaleDateString();

/**
 * Page de listing des dossiers de signature électronique.
 */
const DossierSignatureListe: React.FunctionComponent = function () {
    const dispatch = useDispatch<TiersAppDispatch>();
    const navigate = useNavigate();

    const dataSource = useAppSelector(MainDossierSignatureSelector);
    const { isInitialLoading, isInError } = useInitialLoadingState(dataSource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const { navigateOnClick } = useClickActions({ navigate });

    // Activation du flag de recherche prescripteur
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.ElectronicSign);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    // Chargement initial des données
    React.useEffect(() => {
        dispatch(getDossierSignature({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Nouveau dossier de signature',
                icon: <FaOptionIcon {...newSignIcon.props} />,
                action: () => navigate(signatureNewFullPath),
            },
        ],
        [navigate],
    );

    useDumbActionsContext(contextActions);
    const handleRowClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: DossierSignatureApiObject) => {
            navigateOnClick({ path: `${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: DossierSignatureApiObject) => {
            navigateOnClick({ path: `${id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const columns: ColumnDatatable<DossierSignatureApiObject>[] = React.useMemo(
        () => [
            {
                isDisplayed: true,
                label: 'Sujet',
                render: ({ signataires, sujet }: DossierSignatureApiObject) => (
                    <Stack>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                            sx={{ fontSize: '20px' }}
                        >
                            <SignatureStatus
                                signataires={signataires}
                                tooltip
                            />
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                            sx={{ marginLeft: '15px' }}
                        >
                            {sujet}
                        </Typography>
                    </Stack>
                ),
                field: 'sujet',
                sortable: true,
            },
            {
                isDisplayed: false,
                label: 'Référence externe',
                render: ({ referenceExterne }: DossierSignatureApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {referenceExterne}
                    </Typography>
                ),
            },
            {
                isDisplayed: false,
                label: 'Date de création',
                render: ({ createdAt }: DossierSignatureApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {createdAt ? isoToLocalDate(createdAt) : ''}
                    </Typography>
                ),
                sortable: true,
                field: 'createdAt',
            },
            {
                isDisplayed: false,
                label: 'Date de modification',
                render: ({ updatedAt }: DossierSignatureApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {updatedAt ? isoToLocalDate(updatedAt) : ''}
                    </Typography>
                ),
                sortable: true,
                field: 'updatedAt',
            },
            {
                isDisplayed: true,
                label: 'Créateur',
                render: ({ prescripteur }: DossierSignatureApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {prescripteur}
                    </Typography>
                ),
                sortable: true,
                field: 'prescripteur',
            },
            {
                isDisplayed: true,
                label: 'Email du créateur',
                render: ({ prescripteurEmail }: DossierSignatureApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {prescripteurEmail}
                    </Typography>
                ),
                sortable: true,
                field: 'prescripteurEmail',
            },
            {
                isDisplayed: true,
                label: 'Référentiel',
                render: ({ demandeur }: DossierSignatureApiObject) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {demandeur?.nom}
                    </Typography>
                ),
                sortable: true,
                field: 'demandeur',
            },
            {
                label: 'Actions',
                displayLabel: false,
                render: (item) =>
                    item.id && (
                        <DossierSignatureListeActions
                            signataires={item.signataires}
                            itemId={item.id}
                        />
                    ),
                isDisplayed: true,
            },
        ],
        [],
    );

    const filters: FiltersDatatableList = React.useMemo(
        () => [
            {
                label: 'Dossiers terminé',
                field: 'termine',
                type: 'boolean',
            },
            {
                label: 'Dossiers refusé',
                field: 'refuse',
                type: 'boolean',
            },
            {
                label: 'Sujet',
                field: 'sujet',
                type: 'text',
            },
            {
                label: 'Référence externe',
                field: 'referenceExterne',
                type: 'text',
            },
            {
                label: 'Créateur',
                field: 'prescripteur',
                type: 'text',
            },
            {
                label: 'Email du créateur',
                field: 'prescripteurEmail',
                type: 'text',
            },
            {
                label: 'Demandeur',
                field: 'demandeurNom',
                type: 'text',
            },
        ],
        [],
    );

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des dossiers de signature."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <>
            <SkeletonLoader
                isLoading={isInitialLoading}
                skeleton={
                    <>
                        <HeaderListSkeleton />
                        <ListSkeleton
                            showSearch
                            columns={6}
                        />
                    </>
                }
                isInError={isInError}
                errorChildren={errorContent}
            >
                <ListHeader
                    breadcrumbPath={[
                        { path: 'Signature électronique', link: signatureListeFullPath },
                        'Liste des dossiers de signature',
                    ]}
                    actions={
                        <HeaderActionButton
                            label="Nouveau dossier de signature"
                            icon={newSignIcon}
                            onClick={() => navigate(signatureNewFullPath)}
                            actionType="navigation"
                        />
                    }
                />

                {dataSource && (
                    <StoreDatatable
                        filters={filters}
                        columns={columns}
                        dataSource={dataSource}
                        fetchData={getDossierSignature}
                        localStorageKey="dossierSignatureListe"
                        onClickRow={handleRowClick}
                        onWheelClickRow={handleRowWheelClick}
                    />
                )}
            </SkeletonLoader>

            <Legend icons={signNamedIconsListe} />
        </>
    );
};

export default DossierSignatureListe;
